import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TimelineLite} from "gsap";
import {Life} from "../component/Life";
import {RoadKill} from "../RoadKill";
import {HEART_STATE} from "../component/enums/RoadKillEnums";
import {RoadKillSound} from "../sound/RoadKillSound";
import {RKHelper} from "../utils/RKHelper";

export class LifeView extends PIXI.Container implements StageLayer {
    private _count: number = 0;
    private _isCarStopped: boolean = false;
    private _lifeContainer: PIXI.Container = new PIXI.Container();
    private _heartBase: PIXI.spine.Spine;

    constructor() {
        super();
        this.name = "LifeView";
        this._heartBase = this.createHeartBase();
        this.addChild(this._heartBase, this._lifeContainer);
    }

    public createHeartBase(): PIXI.spine.Spine {
        const spine: PIXI.spine.Spine = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset("heartBase"));
        spine.position.set(74, 84);
        spine.visible = false;
        return spine;
    }

    public reset(): void {
        new TimelineLite().add(RKHelper.playSpineWithTimeline(this._heartBase, 0, "BgOut")).add(() => {
            this._lifeContainer.removeChildren();
            this._count = 0;
            this._heartBase.visible = false;
        })
    }

    public update(heartList: string[]): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._count = this._lifeContainer.children.length;
        tl.add(this.setCount(heartList.length, heartList));
        heartList.forEach((heartType: string, index: number) => {
            if (this._lifeContainer.children[index]) {
                tl.add(() => {
                    (this._lifeContainer.children[index] as Life).isGolden = heartType === "GOLDEN_HEART";
                });
            }
        });
        return tl;
    }

    public onDanger(): TimelineLite {
        this._isCarStopped = false;
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                !this._isCarStopped && this.onDanger();
            }
        });
        const life: Life = this._lifeContainer.children[this._lifeContainer.children.length - 1] as Life;
        if (life) {
            tl.to(life.scale, {duration: 0.1, x: 1.5, y: 1.5});
            tl.to(life.scale, {duration: 0.1, x: 1, y: 1});
        }
        return tl;
    }

    public onAnimalUpgrade(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this._count < RoadKill.model.data.specialSymbolList.length) {
            for (let index: number = 0; index < this._count; index++) {
                const life: Life = this._lifeContainer.children[index] as Life;
                if(life){
                    tl.add(life.shiftRightOnUpgrade(), 0);
                }
            }
            const life: Life = new Life(0);
            life.visible = false;
            this._lifeContainer.addChildAt(life, 0);
            this._count++;
            if (!this._heartBase.visible) {
                this._heartBase.visible = true;
                tl.add(RKHelper.playSpineWithTimeline(this._heartBase, 0, "BgIn"));
            }
            tl.add(() => {
                life.visible = true;
            });
            tl.add(life.setState(HEART_STATE.GOLDEN));
        } else {
            const innerTl: TimelineLite = new TimelineLite();
            tl.add(() => {
                for (let index: number = 0; index < this._lifeContainer.children.length; index++) {
                    if (!(this._lifeContainer.children[index] as Life).isGolden) {
                        const life: Life = this._lifeContainer.children[index] as Life;
                        innerTl.add(life.setState(HEART_STATE.GOLDEN));
                        break;
                    }
                }
            });
            tl.add(() => {
                innerTl.progress(1);
            }, 0.868);
        }
        return tl;
    }

    public onCarMoveDone(isMissed: boolean): void {
        this._isCarStopped = true;
        if (isMissed) {
            const life: Life = this._lifeContainer.children[this._lifeContainer.children.length - 1] as Life;
            if (life) {
                if (life.isGolden) {
                    life.isGolden = false;
                } else {
                    this.setCount(this._lifeContainer.children.length - 1);
                }
            }
        }
    }

    public getCount(): number {
        return this._lifeContainer.children.length;
    }

    private setCount(value: number, heartList?: string[]): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let playSoundOnlyOnce: boolean = true;
        if (value !== this._count) {
            if (value > this._count) {
                let counter: number = 0;
                for (let index: number = this._count; index < value; index++) {
                    const life: Life = new Life(this._lifeContainer.children.length);
                    if (!this._heartBase.visible) {
                        this._heartBase.visible = true;
                        tl.add(RKHelper.playSpineWithTimeline(this._heartBase, 0, "BgIn"));
                    }
                    this._lifeContainer.addChild(life);
                    let isGolden: boolean = false;
                    if ((heartList?.length && heartList[index] === "GOLDEN_HEART")) {
                        isGolden = true;
                    }
                    tl.add(life.setState(isGolden ? HEART_STATE.GOLDEN : HEART_STATE.IN), counter * 0.2);
                    counter++;
                }
            } else {
                for (let index: number = value; index < this._count; index++) {
                    const life: Life = this._lifeContainer.children[this._lifeContainer.children.length - 1] as Life;
                    if (life) {
                        tl.add(life.setState(HEART_STATE.OUT), 0);
                        if (playSoundOnlyOnce) {
                            tl.add(() => {
                                RoadKill.soundPlayer.playTrack(RoadKillSound.HEART_REMOVED);
                            }, 0);
                            playSoundOnlyOnce = false;
                        }
                        tl.add(() => {
                            life.parent?.removeChild(life);
                        });
                    }
                }
            }
            this._count = value;
        }
        return tl;
    }
}
